import { useState } from 'react';
import { Crown, Loader2, Sparkles, Zap, Shield, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Drawer,
  DrawerContent,
} from '@/components/ui/drawer';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import { useSubscription } from '@/hooks/useSubscription';
import { useAdminSettings } from '@/hooks/useAdminSettings';
import { useIsMobile } from '@/hooks/use-mobile';
import { toast } from 'sonner';

interface PlanSelectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const PlanSelectionDialog = ({ open, onOpenChange }: PlanSelectionDialogProps) => {
  const { createCheckoutSession } = useSubscription();
  const { monthlyPrice, lifetimePrice } = useAdminSettings();
  const [isProcessing, setIsProcessing] = useState<'monthly' | 'lifetime' | null>(null);
  const isMobile = useIsMobile();

  const handleCheckout = async (plan: 'monthly' | 'lifetime') => {
    setIsProcessing(plan);
    try {
      await createCheckoutSession(plan);
    } catch (error: any) {
      toast.error(error.message || 'Failed to start checkout');
    } finally {
      setIsProcessing(null);
    }
  };

  const content = (
    <div className="w-full">
      {/* Header */}
      <div className="bg-gradient-to-br from-blue-600 to-purple-600 px-5 py-5 sm:p-6 text-white text-center rounded-t-none sm:rounded-t-2xl">
        <Sparkles className="w-7 h-7 sm:w-8 sm:h-8 mx-auto mb-1.5 text-white/80" />
        <h2 className="text-xl sm:text-2xl font-bold text-white">
          Choose Your Plan
        </h2>
        <p className="text-white/80 text-xs sm:text-sm mt-0.5">
          Start learning today
        </p>
      </div>

      <div className="px-4 py-4 sm:p-6 space-y-3 sm:space-y-4">
        {/* Monthly Plan (Primary) */}
        <button
          onClick={() => handleCheckout('monthly')}
          disabled={!!isProcessing}
          className="w-full text-left p-4 sm:p-5 rounded-2xl border-2 border-green-300 hover:border-green-500 hover:shadow-lg active:scale-[0.98] transition-all relative overflow-hidden group disabled:opacity-60"
        >
          {/* Badge */}
          <div className="absolute top-0 right-0 bg-green-600 text-white text-[9px] sm:text-[10px] px-2.5 py-0.5 sm:px-3 sm:py-1 rounded-bl-lg font-bold uppercase tracking-wider">
            Most Popular
          </div>

          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <h3 className="text-base sm:text-lg font-semibold text-slate-900 group-hover:text-green-600 transition-colors">
                Monthly
              </h3>
              <p className="text-[11px] sm:text-xs text-green-600 font-semibold mt-0.5">
                Full access • Cancel anytime
              </p>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-xs text-slate-400 line-through">$49</p>
              <p className="text-xl sm:text-2xl font-bold text-slate-900">
                ${monthlyPrice}
                <span className="text-xs sm:text-sm font-normal text-slate-500">/mo</span>
              </p>
            </div>
          </div>

          {/* Feature pills */}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {['Full access', '100 AI credits', 'Cancel anytime'].map((text, i) => (
              <span key={i} className="inline-flex items-center gap-1 text-[10px] sm:text-xs text-green-700 bg-green-50 px-2 py-1 rounded-full">
                <Check className="w-3 h-3" /> {text}
              </span>
            ))}
          </div>

          {isProcessing === 'monthly' && (
            <div className="mt-3 flex items-center justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-green-600" />
            </div>
          )}
        </button>

        {/* Lifetime Plan */}
        <button
          onClick={() => handleCheckout('lifetime')}
          disabled={!!isProcessing}
          className="w-full text-left p-4 sm:p-5 rounded-2xl border-2 border-blue-200 hover:border-blue-400 hover:shadow-lg active:scale-[0.98] transition-all relative overflow-hidden group disabled:opacity-60"
        >
          {/* Badge */}
          <div className="absolute top-0 right-0 bg-blue-600 text-white text-[9px] sm:text-[10px] px-2.5 py-0.5 sm:px-3 sm:py-1 rounded-bl-lg font-bold uppercase tracking-wider">
            Best Value
          </div>

          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <h3 className="text-base sm:text-lg font-semibold text-slate-900 flex items-center gap-1.5 group-hover:text-blue-600 transition-colors">
                <Crown className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0" />
                Lifetime
              </h3>
              <p className="text-[11px] sm:text-xs text-slate-500 mt-0.5">
                One-time payment • Everything forever
              </p>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-xs text-slate-400 line-through">$197</p>
              <p className="text-xl sm:text-2xl font-bold text-blue-600">${lifetimePrice}</p>
            </div>
          </div>

          {/* Feature pills */}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {['500+ lessons', 'Faceless Content System', 'Future academies'].map((text, i) => (
              <span key={i} className="inline-flex items-center gap-1 text-[10px] sm:text-xs text-blue-700 bg-blue-50 px-2 py-1 rounded-full">
                <Check className="w-3 h-3" /> {text}
              </span>
            ))}
          </div>

          {isProcessing === 'lifetime' && (
            <div className="mt-3 flex items-center justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
            </div>
          )}
        </button>

        {/* Trust footer */}
        <div className="flex items-center justify-center gap-1.5 pt-1 pb-2">
          <Shield className="w-3.5 h-3.5 text-green-500" />
          <p className="text-[10px] sm:text-[11px] text-slate-400">
            Secure payment via Stripe • 100% money-back guarantee
          </p>
        </div>
      </div>
    </div>
  );

  // Mobile: bottom drawer, Desktop: centered dialog
  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="bg-white border-t-0 rounded-t-3xl max-h-[90vh] overflow-y-auto">
          {content}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-white border-slate-200 rounded-2xl p-0 overflow-hidden">
        {content}
      </DialogContent>
    </Dialog>
  );
};

export default PlanSelectionDialog;
